import { useCallback, useContext, useState } from 'react';

import { Text } from '@chakra-ui/react';
import { MdOutlineLockClock } from 'react-icons/md';

import {
  SecureLinkNotificationReason,
  useOnSecureLinkExpiredSubscription,
} from '../../gql/generated/graphql';
import type { SecureLinkNotification } from '../../gql/generated/graphql';

import NotificationToast from './components/NotificationToast';

import { LDFlags } from '../../constants/experiments';
import useFlag from '../../hooks/useFlag';
import { InteractedContext } from '../../libs/contextLib';

const SecureLinkHandler = () => {
  const secureLinkFlag = useFlag(LDFlags.SECURE_INFO_LINK);
  const hasInteracted = useContext(InteractedContext);

  const [notifications, setNotifications] = useState<
    Map<SecureLinkNotification['deal_id'], SecureLinkNotification>
  >(new Map());

  useOnSecureLinkExpiredSubscription({
    skip: !secureLinkFlag,
    onData: ({ data }) => {
      const notification = data.data?.onSecureLinkExpired;
      // Don't show notifications if the link was revoked.
      if (!notification || notification.reason === SecureLinkNotificationReason.Revoked) {
        return;
      }

      // Always replace with the latest notification for the `deal_id`.
      setNotifications((prev) => {
        const newMap = new Map(prev);
        newMap.set(notification.deal_id, notification);
        return newMap;
      });

      if (hasInteracted) {
        new Audio('/sounds/sms_notification.mp3').play();
      }
    },
  });

  const handleCloseNotification = useCallback((dealId: SecureLinkNotification['deal_id']) => {
    setNotifications((prev) => {
      const newMap = new Map(prev);
      newMap.delete(dealId);
      return newMap;
    });
  }, []);

  return (
    <>
      {Array.from(notifications.values()).map((d) => (
        <NotificationToast
          key={`${d.deal_id}`}
          dealId={d.deal_id}
          slideFadeIn={!!d.first_name}
          title={`${d.first_name} ${d.last_name} - Deal ${d.deal_id}`}
          titleIcon={MdOutlineLockClock}
          titleIconColor={
            d.reason === SecureLinkNotificationReason.Used ? 'green' : 'americanOrange'
          }
          subTitle={`Secure Web Form ${
            d.reason === SecureLinkNotificationReason.Used ? 'Completed' : 'Expired'
          }`}
          details={<Text>Deal is now unlocked.</Text>}
          onClose={() => handleCloseNotification(d.deal_id)}
        />
      ))}
    </>
  );
};

export default SecureLinkHandler;
